import React, { useEffect, useMemo, useState } from 'react';
import FormError from '../feedback/FormError';
import { toast } from 'react-hot-toast';
import utils from '../../utils';
import adminServices from '../../services/AdminServices';
import UserForm from '../dashboard/UserForm';
import { HiTrash } from 'react-icons/hi';
import Select from 'react-select';
import { useAdminData } from '../../context/SessionContext';
import LocationForm from '../dashboard/LocationForm';
import { useLoadScript } from '@react-google-maps/api';
import PatientRecords from './PatientRecords';
import LocationModal from '../dashboard/LocationModal';

const libraries = ["places"];

function PatientModal({ action, activeUser, onReload, setActiveUser }) {
    const [loading, setLoading] = useState(false);
    const [createdUser, setCreatedUser] = useState();
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [location, setLocation] = useState();

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries,
        id: 'google-maps-script',
        async: true,
        defer: true,
    });

    // Patient-specific fields
    const [age, setAge] = useState('');
    const [conditionNotes, setConditionNotes] = useState('');
    const [selectedConditions, setSelectedConditions] = useState([]);    

    const { services } = useAdminData(); 

    const conditionOptions = useMemo(() => {
        return (services || []).map((condition) => ({
            value: condition.id,
            label: condition.name,
        }));
    }, [services]);

    const tabs = useMemo(() => {
        const baseTabs = ['Account', 'Patient'];
        if (action === 1) return [...baseTabs, 'Records', 'Delete'];
        return baseTabs;
    }, [action]);

    const clearData = () => {
        setActiveTab(0);
        setSelectedConditions([]);
        setConditionNotes('');
        setAge('');
        setError(null);
        setActiveUser?.(null);
    };

    useEffect(() => {
        if (action === 1 && activeUser?.id) {
            setAge(activeUser.age || '');
            setConditionNotes(activeUser.condition_notes || '');
            if (activeUser.conditions) {
                const preselected = activeUser.conditions.map(cond => ({
                    value: cond.id,
                    label: cond.name,
                }));
                setSelectedConditions(preselected);
            }
            if (activeUser?.location) {
                setLocation(activeUser.location);
            }
        } else {
            setAge('');
            setConditionNotes('');
            setSelectedConditions([]);
            setLocation(null);
        }
    }, [action, activeUser]);


    const handlePatientSubmit = (e) => {
        e.preventDefault();
        const closeBtn = document.getElementById('patient-create-modal-close-btn');
        setError(null);
        setLoading(true);

        const formData = new FormData();
        formData.append('age', age);
        if (conditionNotes) {
            formData.append('condition_notes', conditionNotes);
        }

        const conditionIds = selectedConditions.map(opt => opt.value);
        conditionIds.forEach(id => {
            formData.append('conditions', id);
        });



        let request;

        if (action === 1) {
            const origLoc = activeUser?.location;
            const hasLocationChanged =
                !origLoc ||
                location?.address !== origLoc.address ||
                location?.city !== origLoc.city ||
                location?.county !== origLoc.county;

            const patientUpdatePromise = () => adminServices.updatePatient(activeUser.id, formData);

            if (!hasLocationChanged) {
                request = patientUpdatePromise();
            } else {
                if (origLoc?.id) {
                    request = adminServices
                        .updateLocation(origLoc.id, {
                            address: location.address,
                            city: location.city,
                            county: location.county,
                            lat: location.lat,
                            lng: location.lng,
                        })
                        .then(() => patientUpdatePromise());
                } else {
                    request = adminServices
                        .addLocation({
                            address: location.address,
                            city: location.city,
                            county: location.county,
                            lat: location.lat,
                            lng: location.lng,
                        })
                        .then(locRes => {
                            formData.append('location', locRes.data.id);
                            return patientUpdatePromise();
                        });
                }
            }
        } else {
            if (!createdUser?.id) {
                toast.error("User must be created first");
                setLoading(false);
                return;
            }
            formData.append("user", createdUser.id);

            const locationToast = toast.loading("Creating location...");

            request = adminServices
                .addLocation(location)
                .then((res) => {
                    toast.success("Location created", { id: locationToast });
                    formData.append("location", res.data?.id);
                    return adminServices.addPatient(formData);
                })
                .catch((err) => {
                    toast.error(utils.toastError, { id: locationToast });
                    throw err;
                });
        }

        request
            .then((res) => {
                toast.success(
                    action === 1
                        ? 'Patient updated successfully'
                        : 'Patient profile created successfully'
                );
                setLoading(false);
                onReload?.();
                closeBtn?.click();
            })
            .catch((err) => {
                setLoading(false);
                console.error("Submission error:", err);
                const errorMsg = err.response?.data || err.message || utils.toastError;
                setError(errorMsg);
                toast.error(typeof errorMsg === 'string' ? errorMsg : utils.toastError);
            });
    };

    const handleDelete = () => {
        const closeBtn = document.getElementById('patient-create-modal-close-btn');
        setDeleteLoading(true);

        adminServices.deletePatient(activeUser.id)
            .then(() => {
                toast.success('Patient deleted successfully');
                setDeleteLoading(false);
                onReload?.();
                closeBtn?.click();
            })
            .catch(() => {
                setDeleteLoading(false);
                toast.error(utils.toastError);
            });
    };

    const changeTab = (idx) => {
        if (action === 1) {
            setActiveTab(idx);
        }
    };

    const handleLocationSuccess = (locationData) => {
        setLocation(locationData);
    };

    const handleUserSuccess = (data) => {
        if (action === 1) {
            onReload?.();
        }
        setCreatedUser(data);
        setActiveTab(1);
    };

    return (
        <div>
            <label htmlFor="patient-create-modal" id="patient-create-open-btn" className="hidden">
                Add / Update Patient
            </label>
            <input type="checkbox" id="patient-create-modal" className="modal-toggle" />

            <div className="modal">
                <div className="modal-box w-8/11 max-w-3xl">
                    <label
                        onClick={clearData}
                        htmlFor="patient-create-modal"
                        id="patient-create-modal-close-btn"
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    >
                        ✕
                    </label>

                    {action === 1 && activeUser?.location?.lat && activeUser?.location?.lng && (
                        <LocationModal
                            activeUser={activeUser}
                            lat={activeUser.location.lat}
                            lng={activeUser.location.lng}
                        />
                    )}

                    <h2 className="text-lg font-bold mb-2">
                        {action === 0 ? 'Add' : 'Edit'} Patient
                        {action === 1 && activeUser && (
                            <span className="text-gray-500 ml-2">
                                {activeUser?.user?.full_name || activeUser?.user?.email}
                            </span>
                        )}
                    </h2>

                    {/* Tabs */}
                    <div className="flex justify-center mb-6">
                        <div className="border-gray-200 dark:border-border-dark">
                            <div className="tabs tabs-boxed justify-center">
                                {tabs.map((tab, idx) => (
                                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                    <a
                                        key={idx}
                                        className={`tab ${activeTab === idx ? 'tab-active bg-primary text-white rounded-lg' : ''}`}
                                        onClick={() => changeTab(idx)}
                                        role="tab"
                                        aria-selected={activeTab === idx}
                                    >
                                        {tab}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    {activeTab === 0 && (
                        <UserForm
                            activeUser={activeUser}
                            action={action}
                            role="Patient"
                            onSuccess={handleUserSuccess}
                        />
                    )}

                    {activeTab === 1 && (
                        <form onSubmit={handlePatientSubmit} className="space-y-3">
                            <h4 className="text-lg font-bold">Patient Info:</h4>

                            {action === 1 && activeUser && (
                                <div className="flex flex-col gap-4 md:flex-row items-center">

                                    <div className="border flex-grow border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark p-3 shadow-sm transition-all hover:shadow-md rounded-xl">
                                        <div className="space-y-2 text-sm">
                                            <p className="font-bold">Overview</p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <p>
                                                    <span className="font-bold mr-2">Age:</span>
                                                    {activeUser?.age || '—'}
                                                </p>
                                                <p className="md:col-span-2">
                                                    <span className="font-bold mr-2">Conditions:</span>
                                                    {activeUser?.conditions?.map(c => c.name).join(', ') || '—'}
                                                </p>
                                                <p className="md:col-span-2">
                                                    <span className="font-bold mr-2">Address:</span>
                                                    {activeUser?.location?.address || activeUser?.location?.city || '--'}
                                                </p>
                                                <p className="md:col-span-2">
                                                    <span className="font-bold mr-2">Notes:</span>
                                                    {activeUser?.condition_notes || '—'}
                                                </p>
                                                <p className="md:col-span-2">
                                                    <span className="font-bold mr-2">Nurse Requests:</span>
                                                    {activeUser?.request_count}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <FormError error={error} />

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Age *</span>
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="150"
                                    value={age}
                                    required
                                    onChange={(e) => setAge(e.target.value)}
                                    className="input input-bordered w-full"
                                    placeholder="Enter patient age"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Medical Conditions</span>
                                </label>
                                <Select
                                    isMulti
                                    value={selectedConditions}
                                    onChange={setSelectedConditions}
                                    options={conditionOptions}
                                    placeholder="Select relevant conditions..."
                                    noOptionsMessage={() => "No conditions available. Add categories first."}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderColor: '#d1d5db',
                                            '&:hover': { borderColor: '#9ca3af' },
                                        }),
                                    }}
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Condition Notes (Optional)</span>
                                </label>
                                <textarea
                                    value={conditionNotes}
                                    onChange={(e) => setConditionNotes(e.target.value)}
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Additional notes about patient conditions..."
                                    rows="3"
                                />
                            </div>

                            <div className="pt-2">
                                <div className="divider"></div>
                            </div>

                            {isLoaded && (
                                <LocationForm
                                    activeUser={activeUser}
                                    location={location}
                                    action={action}
                                    onSuccess={handleLocationSuccess}
                                />
                            )}

                            <div className="">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-75"
                                >
                                    {loading ? 'Saving...' : 'Save Patient Profile'}
                                </button>
                            </div>
                        </form>
                    )}

                    {activeTab === 2 && action === 1 && (
                        <div>
                            <PatientRecords activeUser={activeUser} />
                        </div>
                    )}

                    {activeTab === 3 && action === 1 && (
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-border-light dark:border-border-dark overflow-hidden mt-6">
                            <div className="p-6 md:p-8">
                                <div className="border-l-4 border-error pl-4 py-2">
                                    <h3 className="font-bold text-error">Danger Zone</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                        Deleting this patient will remove their account and all associated data.
                                    </p>
                                    <div className="pt-4">
                                        {deleteLoading ? (
                                            <div className="flex justify-center py-2">
                                                <span className="loading loading-spinner loading-sm"></span>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={handleDelete}
                                                className="flex items-center space-x-2 bg-error text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-error/90 transition-colors"
                                            >
                                                <HiTrash className="h-4 w-4" />
                                                <span>Delete: {activeUser?.user?.full_name || activeUser?.user?.email}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PatientModal;